import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@shared/lib/supabase';

interface OrderRow {
  id: string;
  merchant_id: string;
  expected_amount: number;
  verified_amount: number | null;
  status: string;
  provider: string | null;
  created_at: string;
  merchants: { business_name: string; email: string } | null;
}

const PAGE_SIZE = 25;

const STATUSES: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'expired', label: 'Abandoned' },
  { value: 'usdc_received', label: 'USDC Received' },
  { value: 'forwarding', label: 'Forwarding' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'disputed', label: 'Disputed' },
];

export default function AdminOrders() {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    loadOrders();
  }, [statusFilter]);

  useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  const loadOrders = async () => {
    setLoading(true);
    let query = supabase
      .from('orders')
      .select('id, merchant_id, expected_amount, verified_amount, status, provider, created_at, merchants(business_name, email)')
      .order('created_at', { ascending: false })
      .limit(500);

    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }

    const { data } = await query;
    setOrders((data as OrderRow[]) || []);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    if (!search) return orders;
    const q = search.toLowerCase();
    return orders.filter(
      (o) =>
        o.id.toLowerCase().includes(q) ||
        o.merchants?.business_name?.toLowerCase().includes(q) ||
        o.merchants?.email?.toLowerCase().includes(q) ||
        o.provider?.toLowerCase().includes(q),
    );
  }, [orders, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageOrders = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const totalVolume = orders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + (Number(o.verified_amount) || Number(o.expected_amount) || 0), 0);

  const statusColor = (s: string) => {
    if (s === 'completed') return 'text-green-400 bg-green-400/10 border-green-400/20';
    if (s === 'pending' || s === 'redirected' || s === 'checkout_opened' || s === 'payment_initiated')
      return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
    if (s === 'failed') return 'text-red-400 bg-red-400/10 border-red-400/20';
    if (s === 'expired') return 'text-zinc-200 bg-zinc-800/60 border-zinc-700/50';
    if (s === 'refunded') return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
    return 'text-zinc-400 bg-zinc-800/30 border-zinc-700/30';
  };

  function timeAgo(date: string): string {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(date).toLocaleDateString();
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>All Orders</h1>
        <div className="flex items-center gap-3">
          <div className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
            {filtered.length} order{filtered.length !== 1 ? 's' : ''}
          </div>
          <div className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
            ${totalVolume.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} completed
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>/</span>
          <input
            type="text"
            placeholder="Search by order ID, merchant name, email, or provider..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-7 pr-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors"
            style={{
              border: '1px solid var(--dash-border)',
              background: 'var(--dash-input)',
              color: 'var(--dash-text-secondary)',
            }}
          />
        </div>
      </div>

      <div
        className="flex items-center gap-1 rounded p-1 overflow-x-auto mb-5"
        style={{ background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}
      >
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded transition-colors whitespace-nowrap"
            style={{
              color: statusFilter === s.value ? 'var(--dash-text-primary)' : 'var(--dash-text-dim)',
              background: statusFilter === s.value ? 'var(--dash-active-bg)' : undefined,
            }}
            onMouseEnter={(e) => {
              if (statusFilter !== s.value) e.currentTarget.style.color = 'var(--dash-text-secondary)';
            }}
            onMouseLeave={(e) => {
              if (statusFilter !== s.value) e.currentTarget.style.color = 'var(--dash-text-dim)';
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <p className="font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded p-12 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>
            {search ? 'No orders match your search.' : 'No orders found.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-sans">
                <thead>
                  <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Order</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Merchant</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Amount</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Status</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Provider</th>
                    <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pageOrders.map((order, i) => (
                    <tr
                      key={order.id}
                      className="transition-colors"
                      style={{
                        background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)',
                        borderBottom: '1px solid var(--dash-border-light)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                    >
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                        {order.id.slice(0, 8)}...
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-sans text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                          {order.merchants?.business_name || 'Unknown'}
                        </p>
                        <p className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>
                          {order.merchants?.email || ''}
                        </p>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--dash-text-secondary)' }}>
                        ${Number(order.expected_amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${statusColor(order.status)}`}
                        >
                          {order.status === 'expired' ? 'Abandoned' : order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                        {order.provider || '-'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                        {timeAgo(order.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 rounded font-mono text-xs transition-colors disabled:opacity-30"
                  style={{
                    border: '1px solid var(--dash-border)',
                    background: 'var(--dash-input)',
                    color: 'var(--dash-text-muted)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 rounded font-mono text-xs transition-colors disabled:opacity-30"
                  style={{
                    border: '1px solid var(--dash-border)',
                    background: 'var(--dash-input)',
                    color: 'var(--dash-text-muted)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
